import React, { createContext, useContext, useState, ReactNode } from 'react';
import { ContractABI, ChainConfig, SUPPORTED_CHAINS } from '../utils/abiFetcher';

export interface ProjectConfig {
  name: string;
  contractAddress: string;
  chainId: number;
  chainConfig: ChainConfig;
  contractABI: ContractABI | null;
  selectedFunction?: string;
  pimlicoApiKey?: string;
  isLoading: boolean;
}

interface ProjectContextType {
  project: ProjectConfig | null;
  setProject: (project: ProjectConfig) => void;
  updateProject: (updates: Partial<ProjectConfig>) => void;
  clearProject: () => void;
  getAuthDataFunctions: () => Array<{
    name: string;
    signature: string;
    inputs: any[];
    stateMutability: string;
  }>;
  getChainConfig: () => ChainConfig | null;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

interface ProjectProviderProps {
  children: ReactNode;
}

export const ProjectProvider: React.FC<ProjectProviderProps> = ({ children }) => {
  const [project, setProjectState] = useState<ProjectConfig | null>(null);

  const setProject = (newProject: ProjectConfig) => {
    setProjectState(newProject);
  };

  const updateProject = (updates: Partial<ProjectConfig>) => {
    if (project) {
      const updatedProject = { ...project, ...updates };
      setProjectState(updatedProject);
      
      // If selectedFunction is updated, update it in localStorage for persistence
      if (updates.selectedFunction !== undefined) {
        try {
          const currentProject = localStorage.getItem('current-project');
          if (currentProject) {
            const projectData = JSON.parse(currentProject);
            projectData.selectedFunction = updates.selectedFunction;
            localStorage.setItem('current-project', JSON.stringify(projectData));
          }
        } catch (error) {
          console.warn('Failed to update selectedFunction in localStorage:', error);
        }
      }
    }
  };

  const clearProject = () => {
    setProjectState(null);
  };

  const getAuthDataFunctions = () => {
    if (!project?.contractABI) return [];
    
    // Expected authData tuple structure
    const authDataPattern = /^\(uint256,uint256,bytes32,\(bytes32,bytes,bytes\)\[\],bytes,bool,bytes\)$/;
    
    return project.contractABI.abi
      .map(func => {
        const signature = generateFunctionSignature(func);
        return {
          name: func.name,
          signature,
          inputs: func.inputs,
          stateMutability: func.stateMutability
        };
      })
      .filter(func => {
        // Check if the first parameter matches authData structure
        if (func.inputs.length === 0) return false;
        
        const firstInput = func.inputs[0];
        if (firstInput.type !== 'tuple') return false;
        
        // Check if it has the authData components
        if (!firstInput.components || firstInput.components.length !== 7) return false;
        
        const expectedTypes = ['uint256', 'uint256', 'bytes32', 'tuple[]', 'bytes', 'bool', 'bytes'];
        const actualTypes = firstInput.components.map(comp => comp.type);
        
        return expectedTypes.every((type, index) => actualTypes[index] === type);
      });
  };

  const getChainConfig = () => {
    if (!project) return null;
    return SUPPORTED_CHAINS.find(chain => chain.chainId === project.chainId) || null;
  };

  const generateFunctionSignature = (func: any): string => {
    const inputs = func.inputs.map((input: any) => {
      if (input.components && input.components.length > 0) {
        const tupleTypes = input.components.map((comp: any) => comp.type).join(',');
        return input.type.includes('[]') ? `(${tupleTypes})[]` : `(${tupleTypes})`;
      }
      return input.type;
    }).join(',');
    
    return `${func.name}(${inputs})`;
  };

  return (
    <ProjectContext.Provider value={{
      project,
      setProject,
      updateProject,
      clearProject,
      getAuthDataFunctions,
      getChainConfig
    }}>
      {children}
    </ProjectContext.Provider>
  );
};